import React, { FC, useContext } from "react";
import { GridLegacy as Grid, useMediaQuery, useTheme } from "@mui/material";

import { isSysAdmin } from "../../utility/utils";
import { IBookingRecordOptions, IPreferences } from "../../logic/api";

import EditableField, { EditableFieldTypes } from "../../components/editable/EditableField/EditableField";
import EditableGroup, { EditableGroupType } from "../../components/editable/EditableGroup/EditableGroup";
import NotchedCard from "../../components/cards/NotchedCard/NotchedCard";

import { SessionContext } from "../../utility/useSession";
import styles from "./styles";
import { useTranslation } from "react-i18next";

interface IBookingRecordSettingsProps {
  onSave: (data: Partial<IPreferences>) => Promise<boolean>;
}

export const BookingRecordSettings: FC<IBookingRecordSettingsProps> = ({ onSave }) => {
  const session = useContext(SessionContext);
  const theme = useTheme();
  const isSmallDevice = useMediaQuery(theme.breakpoints.down("xs"));
  const { t } = useTranslation();

  if (!session.current.site) {
    return null;
  }

  if (!isSysAdmin(session)) {
    return null;
  }

  const labelStyle = isSmallDevice
    ? { textAlign: "center" as const }
    : { paddingTop: 40 };

  const ensureBookingRecordOptions = (data: IPreferences): IBookingRecordOptions => {
    if (!data.bookingRecordOptions) {
      data.bookingRecordOptions = { properties: {} };
    }

    return data.bookingRecordOptions;
  };

  return (
    <Grid item xs={12}>
      <NotchedCard label={t("settings.bookingRecOptions")} style={styles.card}>
        <EditableGroup<IPreferences>
          id="bookingRecordOptions"
          editType={EditableGroupType.switchableEditing}
          data={session.current.site.preferences}
          onDataChanged={(data) => onSave({
            bookingRecordOptions: data.bookingRecordOptions
          })}
          testId="booking-record-options"
        >
          <Grid container>
            <Grid item sm={8} xs={12}>
              <div style={labelStyle}><strong>{t("settings.alternativeRef")}</strong></div>
            </Grid>
            <Grid item sm={2} xs={6}>
              <EditableField<IPreferences, boolean>
                useFullWidthWhenEditing={false}
                inputType={EditableFieldTypes.switch}
                label={t("settings.visible")}
                onProvide={(data) => Boolean(data.bookingRecordOptions?.properties.altRef)}
                onChange={(data, value) => ensureBookingRecordOptions(data).properties.altRef = value ? { required: false } : undefined}
                testId="settings-booking-record-alt-ref-visible-switch"
              />
            </Grid>
            <Grid item sm={2} xs={6}>
              <EditableField<IPreferences, boolean>
                useFullWidthWhenEditing={false}
                inputType={EditableFieldTypes.switch}
                label={t("settings.mandatory")}
                onProvide={(data) => Boolean(data.bookingRecordOptions?.properties.altRef?.required)}
                onChange={(data, value) => ensureBookingRecordOptions(data).properties.altRef = { required: Boolean(value) }}
                testId="settings-booking-record-alt-ref-mandatory-switch"
              />
            </Grid>
          </Grid>
        </EditableGroup>
      </NotchedCard>
    </Grid>
  );
};
